import { useMemo, useState } from "react";
import { Building2, MessageSquareText } from "lucide-react";
import { PERSPECTIVES, type PerspectiveId } from "@/lib/perspectives";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { inventoryChatScope } from "@/lib/chat-scope";
import { CHAT_CONTEXT_PACKS } from "@/lib/chat-context-packs";
import { ChatDrawerPanel } from "@/components/chat/ChatDrawerPanel";
import type { Building } from "@/lib/api/building";
import { cardBase } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { KairosLogo } from "./KairosLogo";
import energyImg from "@/assets/energy-consumption.jpg";

const STATUS_BADGE: Record<Building["status"], string> = {
  ok: "bg-success-subtle text-success-subtle-foreground border border-success/20",
  warn: "bg-warning-subtle text-warning-subtle-foreground border border-warning/20",
  alert: "bg-red-500/10 text-red-400 border border-red-500/20",
};

const STATUS_LABEL: Record<Building["status"], string> = {
  ok: "Optimoitu",
  warn: "Potentiaalia",
  alert: "Toimi nyt",
};

// Rich hero banner shown atop every perspective — the look&feel from the old
// Kiinteistökanta dashboard, adapted per perspective (title + tagline + active-scope chip).
export function PerspectiveHero({
  perspectiveId,
}: {
  perspectiveId: PerspectiveId;
}) {
  const p = PERSPECTIVES.find((x) => x.id === perspectiveId) ?? PERSPECTIVES[0];
  const {
    filter,
    isFiltered,
    countLabel,
    activeGroup,
    buildingId,
    groupId,
    selectedBuilding: scopeBuilding,
  } = useInventoryFilter();

  // Hero "Kysy agentilta" CTA — opens the same chat shell as every other entry point,
  // pre-scoped to the current inventory scope plus this perspective's lens id, so the
  // agent resolves a lens agent profile (persona + tool allowlist + graph) instead of
  // the generic assistant with a different header (docs/spec/concepts.md#agent-chat).
  // Never fires the heavy configuration-mode kickoff: this scope carries no `mode`.
  const [chat_open, set_chat_open] = useState(false);
  const context_pack = CHAT_CONTEXT_PACKS[perspectiveId];
  const chat_scope = useMemo(
    () => inventoryChatScope(buildingId, groupId, perspectiveId),
    [buildingId, groupId, perspectiveId],
  );

  return (
    <div
      className={cn(
        cardBase,
        "mb-4 relative overflow-hidden rounded-2xl shadow-elevated",
      )}
    >
      <img
        src={energyImg}
        alt="Kiinteistöjen energiankulutuksen visualisointi"
        width={1024}
        height={512}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/75 to-background/30" />
      <div className="relative p-6 sm:p-8 flex items-start gap-4">
        <KairosLogo size={56} className="mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            <span>BKRY</span>
            <span className="opacity-60">/</span>
            <span>Kairos · Digitaalinen kiinteistöjohtaja</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
            {p.label}
          </h1>
          <p className="text-sm text-foreground mt-1.5 max-w-2xl">
            {p.tagline}
            <span className="text-muted-foreground"> · {countLabel}</span>
          </p>
          {buildingId ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[11px] text-primary">
                <Building2 className="h-3 w-3" />
                Kohde: {scopeBuilding?.name ?? "…"}
                {scopeBuilding && (
                  <span className="text-muted-foreground">
                    · {scopeBuilding.city}
                  </span>
                )}
              </span>
              {scopeBuilding && (
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATUS_BADGE[scopeBuilding.status]}`}
                >
                  {STATUS_LABEL[scopeBuilding.status]}
                </span>
              )}
              {scopeBuilding?.optimization_potential_eur_month != null && (
                <span className="text-[11px] font-semibold text-primary">
                  €{scopeBuilding.optimization_potential_eur_month.toFixed(0)}
                  /kk säästöpotentiaali
                </span>
              )}
            </div>
          ) : (
            isFiltered && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-[11px] text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                Rajaus aktiivinen
                {activeGroup && <span>· {activeGroup.name}</span>}
                {filter.city !== "all" && <span>· {filter.city}</span>}
                {filter.type !== "all" && <span>· {filter.type}</span>}
                {filter.status !== "all" && (
                  <span>
                    ·{" "}
                    {filter.status === "ok"
                      ? "Optimoidut"
                      : filter.status === "warn"
                        ? "Potentiaalia"
                        : "Toimi nyt"}
                  </span>
                )}
              </div>
            )
          )}
        </div>
        <button
          type="button"
          onClick={() => set_chat_open(true)}
          className="shrink-0 inline-flex items-center gap-2 self-start rounded-full
            border border-transparent bg-gradient-accent px-4 py-2 text-sm font-medium
            text-primary-foreground shadow-elevated transition-all duration-300
            hover:border-accent/50
            hover:shadow-[0_0_0_1px_color-mix(in_oklab,var(--accent)_35%,transparent),0_18px_48px_-14px_color-mix(in_oklab,var(--accent)_50%,transparent),0_8px_28px_-10px_color-mix(in_oklab,var(--primary)_30%,transparent)]
            focus-visible:outline-none focus-visible:border-accent/50
            focus-visible:ring-2 focus-visible:ring-accent/40"
        >
          <MessageSquareText className="h-4 w-4 shrink-0" />
          Kysy agentilta
        </button>
      </div>
      <ChatDrawerPanel
        open={chat_open}
        onClose={() => set_chat_open(false)}
        scope={chat_scope}
        buildingId={buildingId ?? undefined}
        contextPack={context_pack}
      />
    </div>
  );
}
